import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import { ApiResources } from "@/api/resources";
import Checkbox from "@/components/CheckBox/Checkbox";
import InputField from "@/components/InputField/InputField";
import MaskedPasswordInput from "@/components/InputField/MaskedPasswordInput";
import PaperBox from "@/components/PaperBox/PaperBox";
import QueryDropdown from "@/components/QueryDropdown/QueryDropdown";
import {
  CONVEY_INTEGRATION_FIELD_NAMES,
  ConveyWebErrors,
  ConveyWebObjectState
} from "@/Pages/Integrations/types";
import { CONVEY_ENDPOINT_URL, ID_TYPE, inputFieldMarginBottom } from "@/shared";
import Grid from "@mui/material/Grid";

import useConveyIntegrationsInputs from "./hooks/useConveyIntegrationsInputs";

interface ConveyIntegrationContentProps {
  optionalPassword?: boolean;
  isLoading: boolean;
  conveyWebObject: ConveyWebObjectState;
  errors: ConveyWebErrors;
  setErrors: Dispatch<SetStateAction<ConveyWebErrors>>;
  setConveyWebObject: Dispatch<SetStateAction<ConveyWebObjectState>>;
  hasInteracted?: boolean;
  setHasInteracted?: Dispatch<SetStateAction<boolean>>;
}

const ConveyIntegrationContent = ({
  optionalPassword,
  isLoading,
  errors,
  conveyWebObject,
  setErrors,
  setConveyWebObject,
  hasInteracted,
  setHasInteracted
}: ConveyIntegrationContentProps) => {
  const { t } = useTranslation();

  const { handleChange: validateOnChange, handleBlur } =
    useConveyIntegrationsInputs(errors, optionalPassword, setErrors);

  const handleChange = (field: CONVEY_INTEGRATION_FIELD_NAMES, value: any) => {
    setConveyWebObject(prev => {
      const getFieldValue = () => {
        if (field === CONVEY_INTEGRATION_FIELD_NAMES.Enabled) {
          return !prev.enabled;
        }

        if (field === CONVEY_INTEGRATION_FIELD_NAMES.IncludeSubCompanies) {
          return !prev.includeSubCompanies;
        }

        return value;
      };

      return {
        ...prev,
        [field]: getFieldValue()
      };
    });

    if (field !== CONVEY_INTEGRATION_FIELD_NAMES.Enabled) {
      validateOnChange(field, value);
    }

    if (setHasInteracted && !hasInteracted) {
      setHasInteracted(true);
    }
  };

  return (
    <Grid
      container
      size={12}
      alignItems="stretch"
      sx={{
        justifyContent: "flex-end"
      }}
      data-testid="convey-integration-content-container-grid"
      spacing={3}
    >
      <Grid size={{ xs: 12, md: 6 }} sx={{ paddingTop: 0 }}>
        <PaperBox
          paperTitle={t("Dialog##convey integration##provider settings title")}
          testId="convey-integration-provider-settings-card"
          customStyling={{ padding: "24px 24px 8px 24px" }}
          paperDescription={t(
            "Dialog##convey integration##provider settings description"
          )}
        >
          <InputField
            data-testid="convey-integration-endpoint-url-field"
            fullWidth
            required
            readOnly
            value={CONVEY_ENDPOINT_URL}
            labelLeft={t("Dialog##endpoint url")}
            placeholder={t("Dialog##endpoint url placeholder")}
            size="medium"
            onChange={e =>
              handleChange(
                CONVEY_INTEGRATION_FIELD_NAMES.EndpointUrl,
                e.target.value
              )
            }
            onBlur={e =>
              handleBlur(
                CONVEY_INTEGRATION_FIELD_NAMES.EndpointUrl,
                e.target.value
              )
            }
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
          />
          <InputField
            data-testid="convey-integration-username-field"
            fullWidth
            required
            value={conveyWebObject[CONVEY_INTEGRATION_FIELD_NAMES.Username]}
            labelLeft={t("Dialog##username")}
            placeholder={t("Dialog##enter username")}
            size="medium"
            onChange={e =>
              handleChange(
                CONVEY_INTEGRATION_FIELD_NAMES.Username,
                e.target.value
              )
            }
            onBlur={e =>
              handleBlur(
                CONVEY_INTEGRATION_FIELD_NAMES.Username,
                e.target.value
              )
            }
            errorText={errors[CONVEY_INTEGRATION_FIELD_NAMES.Username]}
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
          />
          <MaskedPasswordInput
            data-testid="convey-integration-password-field"
            fullWidth
            required={!optionalPassword}
            value={conveyWebObject[CONVEY_INTEGRATION_FIELD_NAMES.Password]}
            name={CONVEY_INTEGRATION_FIELD_NAMES.Password}
            labelLeft={t("Dialog##password")}
            placeholder={t("Dialog##enter password")}
            onChange={handleChange}
            handleBlur={handleBlur}
            errorText={errors[CONVEY_INTEGRATION_FIELD_NAMES.Password]}
            disabled={isLoading}
            handleChange={handleChange}
            customStyle={inputFieldMarginBottom}
          />
        </PaperBox>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }} sx={{ paddingTop: 0 }}>
        <PaperBox
          paperTitle={t("Settings##general settings")}
          testId="convey-integration-general-settings-card"
          customStyling={{
            padding: "24px 24px 8px 24px"
          }}
        >
          <QueryDropdown
            testId="convey-integration-company-dropdown"
            disableClearable
            resource={ApiResources.CompanyList}
            idType={ID_TYPE.Id}
            value={
              conveyWebObject[CONVEY_INTEGRATION_FIELD_NAMES.SelectedCompany]
            }
            readOnly={optionalPassword}
            size="medium"
            fullWidth
            isRequired
            change={(_, val) =>
              handleChange(CONVEY_INTEGRATION_FIELD_NAMES.SelectedCompany, val)
            }
            labelLeft={t("Dialog##company")}
            textFieldParams={{ placeholder: t("Dialog##select") }}
            customStyle={inputFieldMarginBottom}
            isDisabled={isLoading}
          />

          <InputField
            data-testid="convey-integration-name-field"
            size="medium"
            fullWidth
            required
            value={conveyWebObject[CONVEY_INTEGRATION_FIELD_NAMES.Name]}
            labelLeft={t("Dialog##integration name")}
            placeholder={t("Dialog##enter here")}
            onBlur={e =>
              handleBlur(CONVEY_INTEGRATION_FIELD_NAMES.Name, e.target.value)
            }
            onChange={e =>
              handleChange(CONVEY_INTEGRATION_FIELD_NAMES.Name, e.target.value)
            }
            errorText={errors[CONVEY_INTEGRATION_FIELD_NAMES.Name]}
            disabled={isLoading}
            customStyle={inputFieldMarginBottom}
          />

          <Checkbox
            id="integration-disable-integration-checkbox"
            infoIconTooltip={t(
              "Dialog##integrations##deactivate integration##tooltip"
            )}
            infoIconTooltipSize="medium"
            checked={!conveyWebObject.enabled}
            label={t("Dialog##integrations##deactivate integration##title")}
            onChange={e =>
              handleChange(
                CONVEY_INTEGRATION_FIELD_NAMES.Enabled,
                e.target.value
              )
            }
            disabled={isLoading}
            customStyle={{
              ...inputFieldMarginBottom,

              "@media (min-width: 840px) and (max-width: 875px)": {
                marginRight: "28px"
              }
            }}
          />

          <Checkbox
            id="integration-include-sub-companies-checkbox"
            infoIconTooltip={t("Dialog##integrations##include files tooltip")}
            infoIconTooltipSize="medium"
            checked={conveyWebObject.includeSubCompanies}
            label={t(
              "Dialog##integrations##include files from all sub companies"
            )}
            onChange={e =>
              handleChange(
                CONVEY_INTEGRATION_FIELD_NAMES.IncludeSubCompanies,
                e.target.value
              )
            }
            disabled={isLoading}
            customStyle={{
              ...inputFieldMarginBottom,
              "@media (min-width: 840px) and (max-width: 875px)": {
                marginRight: "28px"
              }
            }}
          />
        </PaperBox>
      </Grid>
    </Grid>
  );
};

export default ConveyIntegrationContent;
